import { indexOf } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { AnimatePresence } from "framer-motion";
import classNames from "classnames/bind";

import { AnswerOption } from "./AnswerOption";
import { CustomizedBackground } from "../CustomizedBackground";
import { GameHandlerProps } from "../../types";
import { isTriviaGame } from "../../graphql/helpers/isTriviaGame";
import { QuestionSection } from "./QuestionSection";
import { RESPONSE_CREATE } from "../../graphql/mutations/Response";
import { ResultModal } from "./ResultModal";
import { TimeProvider } from "../../contexts/time";
import { TriviaAnswerOption, TriviaQuestion } from "../../gql/graphql";

import styles from "./TriviaGame.module.scss";
import { useTranslation } from "react-i18next";
import { NicknameModal } from "../shared/NicknameModal";
import { useGlobalConfiguration } from "../../hooks/useGlobalConfiugration";

const cx = classNames.bind(styles);

export interface TriviaAnswer {
  timeInMs: number;
  option?: TriviaAnswerOption;
  question: TriviaQuestion;
}

export const TriviaGame = ({ game, finishGame }: GameHandlerProps) => {
  const { t } = useTranslation("trivia");
  const [responseCreate] = useMutation(RESPONSE_CREATE);
  const { projectNickname, isPreviewMode } = useGlobalConfiguration();

  if (!isTriviaGame(game)) {
    throw new Error("Invalid game type");
  }

  const {
    configuration: {
      questions,
      timeToCompleteInSeconds: gameTime,
      gameVisuals,
      showLeaderboard,
      leaderboardConfiguration,
    },
  } = game;

  const [currentQuestion, setCurrentQuestion] = useState<TriviaQuestion>(questions[0]);
  const [isChangingQuestion, setIsChangingQuestion] = useState(false);
  const [isTimeFinished, setIsTimeFinished] = useState(false);
  const [answers, setAnswers] = useState<TriviaAnswer[]>([]);
  const [nickname, setNickname] = useState<string | undefined>(projectNickname || undefined);

  const startTime = useMemo(() => new Date().toISOString(), []);
  const isGameStopped = answers.length === questions.length || isChangingQuestion || isTimeFinished;
  const lastAnswer = answers[answers.length - 1] || {};

  useEffect(() => {
    if (projectNickname) {
      setNickname(projectNickname);
    }
  }, [projectNickname]);

  const handleSubmitAnswer = (option?: TriviaAnswerOption, time?: number) => {
    setAnswers((prevAnswers) => [...prevAnswers, { option, question: currentQuestion, timeInMs: time ?? 0 }]);
    setIsChangingQuestion(true);
  };

  const handleFinishGame = async () => {
    !isChangingQuestion && setIsChangingQuestion(true);

    if (isPreviewMode) {
      return finishGame();
    }

    const { data } = await responseCreate({
      variables: {
        data: {
          gameId: game.id,
          nickname,
          triviaScores: answers
            .filter(({ option }) => Boolean(option))
            .map((answer) => ({
              triviaAnswerOptionId: (answer.option as TriviaAnswerOption).id,
              triviaQuestionId: answer.question.id,
              timeInMs: answer.timeInMs,
            })),
          endTime: new Date().toISOString(),
          startTime,
        },
      },
    });

    finishGame(String(data?.responseCreate.slug));
  };

  const triggerNextQuestion = () => {
    const nextQuestion = questions[indexOf(questions, currentQuestion) + 1];

    if (!nextQuestion || isTimeFinished) {
      return handleFinishGame();
    }

    setIsChangingQuestion(false);
    setCurrentQuestion(nextQuestion);
  };

  const handleGameTimeEnd = () => {
    setIsTimeFinished(true);
    handleSubmitAnswer();
  };

  const isLeaderboardVisible = showLeaderboard && !nickname;
  const showGame = !showLeaderboard || nickname;

  return (
    <CustomizedBackground config={gameVisuals}>
      <div className={cx("triviaGame", { isLeaderboardVisible })}>
        <NicknameModal
          isOpen={isLeaderboardVisible}
          gameId={game.id}
          themeColor={gameVisuals.themeColor}
          configuration={leaderboardConfiguration}
          onContinue={setNickname}
        />

        {showGame && (
          <>
            <TimeProvider onTimeEnd={handleGameTimeEnd} isPaused={isGameStopped} time={gameTime * 1000}>
              <QuestionSection
                question={currentQuestion}
                score={answers.reduce((acc, { option }) => (option?.isCorrect ? acc + option.points : acc), 0)}
                allQuestions={questions}
              />
              <div className={styles.answersWrapper} id="answersWrapper">
                {currentQuestion.options.map((option) => (
                  <AnswerOption
                    disabled={isGameStopped}
                    handleAnswer={handleSubmitAnswer}
                    key={option.id}
                    option={option}
                    showCorrectness={isChangingQuestion}
                  />
                ))}
              </div>
            </TimeProvider>
            <AnimatePresence>
              {(isChangingQuestion || isTimeFinished) && lastAnswer && (
                <ResultModal
                  isCorrect={lastAnswer.option?.isCorrect ?? false}
                  explanation={lastAnswer.option?.explanation ?? t("timeIsUp")}
                  isLastQuestion={answers.length === questions.length || isTimeFinished}
                  onContinue={triggerNextQuestion}
                  theme={gameVisuals.themeColor}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </CustomizedBackground>
  );
};
